import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
	CheckinOrderFragment,
	CheckinOrderStatus,
	useGetCheckinOrderQuery,
	useResetOrderPaymentBlockMutation
} from '@websky/graphql';
import { getErrorMessage, NormalizedError } from '@shared/error';
import { useResetPaymentBlock, usePaymentPolling } from '@features/payment';
import { useHistory } from '@shared/routing';

import { CheckinGoal, reachGoal } from '../../../../../analytics';
import { getFunnelParameters } from '../../../../../DataLayerAnalytics/utils';
import { getCheckinIsBlockedForPayment, getCheckinOrder, getCheckinOrderId } from '../../../store/order/selectors';
import { setCheckinOrder } from '../../../store/order/actions';
import { CheckinStep } from '../../../types';

export const useCheckinPaymentFailedHandlers = ({
	setStep,
	goToNextStep
}: {
	setStep: (step: CheckinStep, searchParams?: string) => void;
	goToNextStep: (currentStep: CheckinStep, replace?: boolean, ignoreBlockedForPayment?: boolean) => void;
}) => {
	const dispatch = useDispatch();
	const orderId = useSelector(getCheckinOrderId);
	const isBlockedForPayment = useSelector(getCheckinIsBlockedForPayment);

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<NormalizedError | null>(null);
	/* Инкремент в onErrorConfirm пересоздаёт onFlowStart и перезапускает flow в PaymentResultFlow */
	const [flowStartKey, setFlowStartKey] = useState(0);

	const { refetch } = useGetCheckinOrderQuery({ fetchPolicy: 'no-cache', skip: true });
	const [resetOrderPaymentBlock] = useResetOrderPaymentBlockMutation();

	const resetBlock = useCallback(
		async (id: string) => {
			await resetOrderPaymentBlock({ variables: { id } });
		},
		[resetOrderPaymentBlock]
	);

	const refetchOrder = useCallback(
		async (id: string) => {
			const { data } = await refetch({ id });

			return data?.CheckinOrder ?? null;
		},
		[refetch]
	);

	const applyOrder = useCallback(
		(order: CheckinOrderFragment) => {
			dispatch(setCheckinOrder(order));
		},
		[dispatch]
	);

	const handleSetError = useCallback((error: unknown) => {
		if (error === null) {
			setError(null);

			return;
		}

		setError({
			details: [{ label: '', value: getErrorMessage(error) }]
		});
	}, []);

	const { resetPaymentBlockAndRefetchOrder } = useResetPaymentBlock<CheckinOrderFragment>({
		orderId,
		isBlockedForPayment,
		resetBlock,
		refetchOrder,
		applyOrder,
		setLoading,
		setError: handleSetError
	});

	const onFlowStart = useCallback(resetPaymentBlockAndRefetchOrder, [
		flowStartKey,
		resetPaymentBlockAndRefetchOrder
	]);

	const baseBack = useCallback(
		async (onSuccess: () => void) => {
			const isSuccess = await resetPaymentBlockAndRefetchOrder();

			if (isSuccess) {
				onSuccess();
			}
		},
		[resetPaymentBlockAndRefetchOrder]
	);

	const onMount = useCallback(() => {
		reachGoal(CheckinGoal.PaymentCancelled);
	}, []);

	const onMountThenCheckinCompleted = useCallback(() => {
		onMount();

		goToNextStep(CheckinStep.Passengers, true);
	}, [onMount, goToNextStep]);

	const goToPaymentAfterFailedPayment = useCallback(() => {
		baseBack(() => setStep(CheckinStep.Payment));
	}, [baseBack, setStep]);

	const goToOrderAfterFailedPayment = useCallback(() => {
		baseBack(() => {
			/**
			 * checkinIsBlockedForPayment в замыкании useSteps (goToNextStep) ещё указывает
			 * на состояние до снятия блокировки, поэтому передаём ignoreBlockedForPayment, чтобы
			 * getNextStepEnum не увёл повторно на шаг оплаты. Шаг вычисляется через доступные
			 * шаги заказа, а не хардкодится на Baggage, которого может не быть у заказа
			 * (например, при заморозке цены)
			 */
			goToNextStep(CheckinStep.Passengers, false, true);
		});
	}, [baseBack, goToNextStep]);

	const clearError = useCallback(() => {
		setError(null);
	}, []);

	const onErrorConfirm = useCallback(() => {
		setError(null);

		setFlowStartKey(prev => prev + 1);
	}, []);

	const onErrorCancel = useCallback(() => {
		setError(null);

		setStep(CheckinStep.Passengers);

		window.location.reload();
	}, [goToOrderAfterFailedPayment, setStep]);

	return {
		isLoading: loading,
		error,
		clearError,
		onMount,
		onMountThenCheckinCompleted,
		onFlowStart,
		goToPaymentAfterFailedPayment,
		goToOrderAfterFailedPayment,
		onErrorConfirm,
		onErrorCancel,
		onErrorClose: onErrorCancel
	};
};

export const useCheckinPaymentSuccessHandlers = ({
	setStep,
	goToNextStep
}: {
	setStep: (step: CheckinStep, searchParams?: string) => void;
	goToNextStep: (currentStep: CheckinStep, replace?: boolean, ignoreBlockedForPayment?: boolean) => void;
}) => {
	const history = useHistory();
	const dispatch = useDispatch();
	const order = useSelector(getCheckinOrder);
	const orderId = useSelector(getCheckinOrderId);

	const [error, setError] = useState<NormalizedError | null>(null);
	const [flowStartKey, setFlowStartKey] = useState(0);

	const { isPolling, startPolling, stopPolling } = usePaymentPolling<CheckinOrderFragment>();

	const { refetch: getOrder } = useGetCheckinOrderQuery({ skip: true, fetchPolicy: 'no-cache' });

	const refetchOrder = useCallback(async () => {
		const { data } = await getOrder({ id: orderId });

		return data?.CheckinOrder ?? null;
	}, [getOrder, orderId]);

	const onFlowStart = useCallback((): Promise<boolean> => {
		setError(null);

		return new Promise(resolve => {
			startPolling({
				fetcher: refetchOrder,
				maxAttempts: 5,
				shouldStop: checkinOrder =>
					[CheckinOrderStatus.Confirmed, CheckinOrderStatus.Expired].includes(checkinOrder.status),
				onSuccess: checkinOrder => {
					dispatch(setCheckinOrder(checkinOrder));
					resolve(true);
				},
				onTimeout: () => {
					history.push({ pathname: `/${orderId}/${CheckinStep.Payment}/failed` });
					resolve(false);
				},
				onError: error => {
					setError({
						details: [{ label: '', value: getErrorMessage(error) }]
					});
					resolve(false);
				}
			});
		});
	}, [flowStartKey, dispatch, history, orderId, refetchOrder, startPolling]);

	const onMount = useCallback(() => {
		reachGoal(CheckinGoal.Paid);
		reachGoal(CheckinGoal.CompleteWithPayment, { funnel_checkin: getFunnelParameters(order) });
	}, [order]);

	const onMountThenCheckinCompleted = useCallback(() => {
		onMount();

		goToNextStep(CheckinStep.Passengers, true);
	}, [goToNextStep, onMount]);

	const goToOrderAfterSuccessPayment = useCallback(() => {
		setStep(CheckinStep.Passengers);
	}, [setStep]);

	const clearError = useCallback(() => {
		setError(null);
	}, []);

	const onErrorConfirm = useCallback(() => {
		setError(null);

		setFlowStartKey(prev => prev + 1);
	}, []);

	const onErrorCancel = useCallback(() => {
		setError(null);

		goToOrderAfterSuccessPayment();

		window.location.reload();
	}, [goToOrderAfterSuccessPayment]);

	return {
		isLoading: isPolling,
		onMount,
		onMountThenCheckinCompleted,
		onFlowStart,
		onFlowAbort: stopPolling,
		error,
		clearError,
		onErrorConfirm,
		onErrorCancel,
		onErrorClose: onErrorCancel,
		goToOrderAfterSuccessPayment
	};
};
